import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiUrl } from './constants';
import { HttpErrorResponse } from '@angular/common/http';
import { RoutesEnum } from '@src/app/enums';
import { Router } from '@angular/router';
import { IBankomat } from '../types/api';

@Injectable({ providedIn: 'root' })
export class BranchesAndAtmsService {
  private isBranchesLoadingSubject = new BehaviorSubject<boolean>(false);
  isBranchesLoading$ = this.isBranchesLoadingSubject.asObservable();

  private isBankomatsLoadingSubject = new BehaviorSubject<boolean>(true);
  isBankomatsLoading$ = this.isBankomatsLoadingSubject.asObservable();

  private branchesSubject = new BehaviorSubject<Record<string, any>[]>([]);
  branches$ = this.branchesSubject.asObservable();

  private branchesOptionsSubject = new BehaviorSubject<Record<string, any>[]>(
    [],
  );
  branchesOptions$: Observable<Record<string, any>[]> =
    this.branchesOptionsSubject.asObservable();

  private activeBranchSubject = new BehaviorSubject<Record<string, any>>({});
  activeBranch$ = this.activeBranchSubject.asObservable();

  private bankomatsSubject = new BehaviorSubject<IBankomat[]>([]);
  bankomats$ = this.bankomatsSubject.asObservable();

  constructor(
    @Inject(Router) private router: Router,
    private apiService: ApiService,
  ) {}

  get isBranchesLoading(): boolean {
    return this.isBranchesLoadingSubject.value;
  }

  get isBankomatsLoading(): boolean {
    return this.isBankomatsLoadingSubject.value;
  }

  get branches(): Record<string, any>[] {
    return this.branchesSubject.value;
  }

  get branchesOptions(): Record<string, any>[] {
    return this.branchesOptionsSubject.value;
  }

  get activeBranch(): Record<string, any> {
    return this.activeBranchSubject.value;
  }

  get bankomats(): IBankomat[] {
    return this.bankomatsSubject.value;
  }

  setIsBranchesLoading(value: boolean): void {
    this.isBranchesLoadingSubject.next(value);
  }

  setIsBankomatsLoading(value: boolean): void {
    this.isBankomatsLoadingSubject.next(value);
  }

  setBranches(list: Record<string, any>[]): void {
    this.branchesSubject.next(list);
  }

  setBranchesOptions(data: Record<string, any>[]): void {
    this.branchesOptionsSubject.next(data);
  }

  setActiveBranch(item: Record<string, any>): void {
    this.activeBranchSubject.next(item);
  }

  setBankomats(list: IBankomat[]): void {
    this.bankomatsSubject.next(list);
  }

  /**
   * @description Handling API call errors
   * @param {HttpErrorResponse} error
   */
  handleError(error: HttpErrorResponse): void {
    this.isBranchesLoadingSubject.next(false);
    this.isBankomatsLoadingSubject.next(false);

    if (
      error.error?.statusCode === 404 ||
      error.error?.status === 404 ||
      error?.status === 404
    ) {
      this.router.navigate(['/' + RoutesEnum.NOT_FOUND], {
        skipLocationChange: true,
      });
    } else if (error.error?.message) {
      console.error(error.error?.message);
    }
  }

  getBranches(
    params: Record<string, string | number>,
    options: { withLoading?: boolean } = { withLoading: true },
  ): Observable<Record<string, any>[]> {
    if (options?.withLoading) {
      this.isBranchesLoadingSubject.next(true);
    }

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getBranches,
      params,
    });
  }

  getBankomats(
    params: Record<string, string | number>,
    options: { withLoading?: boolean } = { withLoading: true },
  ): Observable<Record<string, any>> {
    if (options?.withLoading) {
      this.isBankomatsLoadingSubject.next(true);
    }

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getBankomats,
      params,
    });
  }
}
